import type { QueryClient } from '@tanstack/react-query';
import type {
  AudienceListItem,
  AudienceListResponse,
  AudienceDetailResponse,
  SaveAudienceRequest,
  SaveAudienceResponse
} from '@/interfaces/AudienceManifest.interface';
import { isValidDate } from '@/utils/date';
import { audienceManifestQueryKeys } from './query-keys';

const FALLBACK_STORAGE_KEY = 'manifest-engine-audience-list-fallback';
const MAX_FALLBACK_ITEMS = 100;

export function getFallbackStorageKey(
  tenantId?: string | null,
  env = import.meta.env.MODE
): string {
  const tenant =
    tenantId ||
    (typeof window !== 'undefined'
      ? window.localStorage.getItem('loggedin_tenant_company_uuid')
      : null) ||
    'global';
  return `insights_audience_fallback_${tenant}_${env}`;
}

export function normalizeAudienceListStatus(
  status: string | undefined
): AudienceListItem['status'] {
  const value = (status ?? '').toLowerCase();
  if (value === 'published') return 'published';
  if (value === 'archived') return 'archived';
  return 'draft';
}

export function isAudienceReadOnly(audience: {
  frozen_at?: string | null;
  status?: string;
}): boolean {
  return Boolean(audience.frozen_at) || audience.status === 'published';
}

export function audienceStatusLabel(
  status: AudienceListItem['status']
): string {
  if (status === 'published') return 'Published';
  if (status === 'archived') return 'Archived';
  return 'Draft';
}

export function audienceRequestErrorMessage(
  data:
    | {
        detail?: unknown;
        message?: unknown;
        error?: unknown;
        response?: { status?: number; data?: unknown };
        status?: number;
      }
    | unknown,
  fallback: string,
  status?: number
): string {
  const d = data as
    | {
        detail?: unknown;
        message?: unknown;
        error?: unknown;
        response?: { status?: number; data?: unknown };
        status?: number;
      }
    | undefined;

  const statusCode = status ?? d?.response?.status ?? d?.status;
  if (statusCode === 429) {
    return 'Too many requests. Please slow down and try again.';
  }

  const responseData = d?.response?.data as
    | { detail?: unknown; message?: unknown; error?: unknown }
    | undefined;

  const candidates = [
    d?.detail,
    d?.message,
    d?.error,
    responseData?.detail,
    responseData?.message,
    responseData?.error
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return fallback;
}

export function audienceListItemCount(item: AudienceListItem): number | null {
  const count = item.member_count ?? item.total_volume;
  if (count == null || Number.isNaN(count)) return null;
  return count;
}

export function audienceListDateSortValue(value: string | undefined): number {
  if (!value || !isValidDate(value)) return 0;
  return new Date(value).getTime();
}

export function sortAudienceListItems(
  items: AudienceListItem[],
  field: 'created_at' | 'name' | 'updated_at',
  direction: 'asc' | 'desc'
): AudienceListItem[] {
  const sorted = [...items].sort((a, b) => {
    if (field === 'name') {
      return (a.audience_name ?? '').localeCompare(b.audience_name ?? '');
    }
    const aValue = audienceListDateSortValue(a[field]);
    const bValue = audienceListDateSortValue(b[field]);
    return aValue - bValue;
  });
  return direction === 'desc' ? sorted.reverse() : sorted;
}

function mergeAudienceListTotalVolume(
  primary: AudienceListItem,
  secondary: AudienceListItem
): number | null | undefined {
  const primaryCount = audienceListItemCount(primary);
  const secondaryCount = audienceListItemCount(secondary);
  if (primaryCount != null) return primaryCount;
  if (secondaryCount != null) return secondaryCount;
  return primary.total_volume ?? secondary.total_volume;
}

function mergeAudienceListStatus(
  existing: AudienceListItem['status'],
  incoming: AudienceListItem['status']
): AudienceListItem['status'] {
  if (existing === 'published' || incoming === 'published') return 'published';
  return incoming;
}

function mergeAudienceListItems(
  primary: AudienceListItem,
  secondary: AudienceListItem
): AudienceListItem {
  const preferSecondary =
    audienceListDateSortValue(secondary.updated_at) >
    audienceListDateSortValue(primary.updated_at);
  const base = preferSecondary ? secondary : primary;
  const other = preferSecondary ? primary : secondary;

  return normalizeAudienceListItem({
    ...other,
    ...base,
    status: mergeAudienceListStatus(primary.status, secondary.status),
    updated_at: mergeUpdatedAt(primary.updated_at, secondary.updated_at),
    total_volume: mergeAudienceListTotalVolume(primary, secondary)
  });
}

export function normalizeAudienceListItem(
  item: Partial<AudienceListItem> & { audience_id?: string }
): AudienceListItem {
  return {
    audience_id: item.audience_id ?? '',
    audience_name: item.audience_name ?? '',
    audience_type: item.audience_type === 'static' ? 'static' : 'dynamic',
    status: normalizeAudienceListStatus(item.status),
    created_by: item.created_by ?? '',
    created_at: item.created_at ?? '',
    updated_at: item.updated_at ?? '',
    ...(item.total_volume != null ? { total_volume: item.total_volume } : {}),
    ...(item.member_count != null ? { member_count: item.member_count } : {}),
    ...(item.frozen_at ? { frozen_at: item.frozen_at } : {}),
    ...(item.parent_audience_id
      ? { parent_audience_id: item.parent_audience_id }
      : {}),
    ...(item.parent_audience_name
      ? { parent_audience_name: item.parent_audience_name }
      : {})
  };
}

export function normalizeAudienceListResponse(
  response: AudienceListResponse
): AudienceListResponse {
  const records = (response.records ?? [])
    .map(normalizeAudienceListItem)
    .filter((item) => item.status !== 'archived');
  return {
    ...response,
    records,
    total_records: Math.max(response.total_records ?? 0, records.length)
  };
}

export function audienceDetailCount(
  detail: AudienceDetailResponse
): number | null {
  if (detail.member_count != null && !Number.isNaN(detail.member_count)) {
    return detail.member_count;
  }
  if (detail.total_audience != null && !Number.isNaN(detail.total_audience)) {
    return detail.total_audience;
  }
  if (
    detail.contactability?.total_audience != null &&
    !Number.isNaN(detail.contactability.total_audience)
  ) {
    return detail.contactability.total_audience;
  }
  if (detail.total_volume != null && !Number.isNaN(detail.total_volume)) {
    return detail.total_volume;
  }
  if (
    detail.upload_record_count != null &&
    !Number.isNaN(detail.upload_record_count)
  ) {
    return detail.upload_record_count;
  }
  return null;
}

export function audienceListItemFromDetail(
  detail: AudienceDetailResponse
): AudienceListItem {
  const totalVolume = audienceDetailCount(detail);

  return normalizeAudienceListItem({
    audience_id: detail.audience_id,
    audience_name: detail.audience_name,
    audience_type: detail.audience_type === 'static' ? 'static' : 'dynamic',
    status: normalizeAudienceListStatus(detail.status),
    created_by: detail.created_by,
    created_at: detail.created_at,
    updated_at: detail.updated_at,
    ...(totalVolume != null ? { total_volume: totalVolume } : {}),
    ...(detail.member_count != null
      ? { member_count: detail.member_count }
      : {}),
    ...(detail.frozen_at ? { frozen_at: detail.frozen_at } : {}),
    ...(detail.parent_audience_id
      ? { parent_audience_id: detail.parent_audience_id }
      : {}),
    ...(detail.parent_audience_name
      ? { parent_audience_name: detail.parent_audience_name }
      : {})
  });
}

export function audienceListItemFromSave(
  data: SaveAudienceResponse,
  request: SaveAudienceRequest,
  createdBy = ''
): AudienceListItem {
  const savedAt = data.updated_at ?? new Date().toISOString();
  return {
    audience_id: data.audience_id,
    audience_name: data.audience_name,
    audience_type: request.audience_type ?? 'dynamic',
    status: normalizeAudienceListStatus(request.status ?? data.status),
    created_by: createdBy,
    created_at: savedAt,
    updated_at: savedAt,
    total_volume: data.total_volume
  };
}

export function audienceListItemFromUpdate(
  existing: AudienceListItem,
  data: SaveAudienceResponse,
  request: SaveAudienceRequest
): AudienceListItem {
  const updatedAt = data.updated_at ?? new Date().toISOString();
  return normalizeAudienceListItem({
    ...existing,
    audience_name: data.audience_name,
    audience_type: request.audience_type ?? existing.audience_type,
    status: normalizeAudienceListStatus(request.status ?? data.status),
    updated_at: updatedAt,
    total_volume: data.total_volume ?? existing.total_volume
  });
}

function mergeUpdatedAt(existing: string, incoming: string): string {
  const existingTime = audienceListDateSortValue(existing);
  const incomingTime = audienceListDateSortValue(incoming);
  if (existingTime === 0) return incoming;
  if (incomingTime === 0) return existing;
  return existingTime >= incomingTime ? existing : incoming;
}

function readFallbackAudienceListItems(): AudienceListItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const key = getFallbackStorageKey();
    const raw =
      window.localStorage.getItem(key) ||
      window.localStorage.getItem(FALLBACK_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AudienceListItem[];
    return Array.isArray(parsed) ? parsed.map(normalizeAudienceListItem) : [];
  } catch {
    return [];
  }
}

function writeFallbackAudienceListItems(items: AudienceListItem[]) {
  if (typeof window === 'undefined') return;
  const key = getFallbackStorageKey();
  window.localStorage.setItem(
    key,
    JSON.stringify(items.slice(0, MAX_FALLBACK_ITEMS))
  );
}

export function getFallbackAudienceListItem(
  audienceId: string
): AudienceListItem | null {
  if (!audienceId) return null;
  return (
    readFallbackAudienceListItems().find(
      (item) => item.audience_id === audienceId
    ) ?? null
  );
}

export function findAudienceListItem(
  queryClient: QueryClient,
  audienceId: string
): AudienceListItem | null {
  if (!audienceId) return null;

  const list = queryClient.getQueryData<AudienceListResponse>(
    audienceManifestQueryKeys.audienceList()
  );
  const fromList =
    list?.records?.find((item) => item.audience_id === audienceId) ?? null;
  if (fromList) return fromList;

  return getFallbackAudienceListItem(audienceId);
}

export function rememberFallbackAudienceListItem(item: AudienceListItem) {
  const existing = readFallbackAudienceListItems().filter(
    (entry) => entry.audience_id !== item.audience_id
  );
  writeFallbackAudienceListItems([
    normalizeAudienceListItem(item),
    ...existing
  ]);
}

export function removeFallbackAudienceListItem(audienceId: string) {
  writeFallbackAudienceListItems(
    readFallbackAudienceListItems().filter(
      (item) => item.audience_id !== audienceId
    )
  );
}

export function mergeFallbackAudienceList(
  response: AudienceListResponse
): AudienceListResponse {
  const apiRecords = (response.records ?? []).map(normalizeAudienceListItem);
  const fallbackById = new Map(
    readFallbackAudienceListItems().map((item) => [item.audience_id, item])
  );

  const mergedApiRecords = apiRecords.map((apiItem) => {
    const fallback = fallbackById.get(apiItem.audience_id);
    if (!fallback) return apiItem;
    fallbackById.delete(apiItem.audience_id);
    return mergeAudienceListItems(apiItem, fallback);
  });

  const records = [...Array.from(fallbackById.values()), ...mergedApiRecords];

  return {
    ...response,
    records,
    total_records: Math.max(response.total_records ?? 0, records.length)
  };
}

export function upsertAudienceListCache(
  queryClient: QueryClient,
  item: AudienceListItem
) {
  const normalized = normalizeAudienceListItem(item);
  queryClient.setQueryData<AudienceListResponse>(
    audienceManifestQueryKeys.audienceList(),
    (current) => {
      const records = current?.records ?? [];
      const index = records.findIndex(
        (entry) => entry.audience_id === normalized.audience_id
      );
      const nextRecords =
        index === -1
          ? [normalized, ...records]
          : records.map((entry, entryIndex) => {
              if (entryIndex !== index) return entry;
              const merged = { ...entry, ...normalized };
              return normalizeAudienceListItem({
                ...merged,
                status: mergeAudienceListStatus(
                  entry.status,
                  normalized.status
                ),
                updated_at: mergeUpdatedAt(
                  entry.updated_at,
                  normalized.updated_at
                ),
                total_volume: mergeAudienceListTotalVolume(entry, normalized)
              });
            });

      return {
        current_page: current?.current_page ?? 1,
        page_size: current?.page_size ?? 100,
        total_pages: current?.total_pages ?? 1,
        total_records: Math.max(
          current?.total_records ?? 0,
          nextRecords.length
        ),
        records: nextRecords
      };
    }
  );
}

export function removeAudienceFromListCache(
  queryClient: QueryClient,
  audienceId: string
) {
  queryClient.setQueryData<AudienceListResponse>(
    audienceManifestQueryKeys.audienceList(),
    (current) => {
      if (!current) return current;
      const records = current.records.filter(
        (entry) => entry.audience_id !== audienceId
      );
      const removedCount = current.records.length - records.length;

      return {
        ...current,
        records,
        total_records: Math.max(0, (current.total_records ?? 0) - removedCount)
      };
    }
  );
}
